import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onMessageListener } from '@/config/firebase';
import { useToast } from '@/hooks/useToast';
import { NotificationService } from '@/services/notification.service';

interface NotificationContextValue {
    isInitialized: boolean;
    initialize: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const { toast } = useToast();

    const initialize = useCallback(async () => {
        if (isInitialized) return;
        await NotificationService.getInstance().initialize();
        setIsInitialized(true);
    }, [isInitialized]);

    useEffect(() => {
        if (!isInitialized) return;

        const setupMessageListener = async () => {
            try {
                const payload = await onMessageListener();
                if (payload) {
                    console.log("Foreground message received:", payload);
                    const { title, body } = NotificationService.parsePayload(payload);
                    toast({ title, description: body });
                }
            } catch (error) {
                console.error('Error handling message:', error);
            } finally {
                // Re-setup listener
                setupMessageListener();
            }
        };

        setupMessageListener();

        return () => {
            // Cleanup if needed
        };
    }, [isInitialized, toast]);

    return (
        <NotificationContext.Provider value={{
            isInitialized,
            initialize,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};